import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { MobileLayout } from './MobileLayout';
import { LoadingFallback } from '@/components/common/LoadingFallback';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';

// Auth pages
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { RegisterPage } from '@/features/auth/pages/RegisterPage';

// Mobile-optimized components
import { MobileStoryReader } from '../story/MobileStoryReader';
import { MobileGenerateStoryPage } from '../story/MobileGenerateStoryPage';
import { MobileLibraryPage } from '../library/MobileLibraryPage';
import { MobileAssignmentsPage } from '../assignments/MobileAssignmentsPage';
import { MobileQuizPage } from '../quiz/MobileQuizPage';
import { MobileResultsPage } from '../quiz/MobileResultsPage';
import { MobileFlashcardsPage } from '../flashcards/MobileFlashcardsPage';
import { MobileStudyPage } from '../flashcards/MobileStudyPage';
import { MobileParentDashboard } from '../parent/MobileParentDashboard';
import { MobileAssignmentCreator } from '../parent/MobileAssignmentCreator';
import { MobileChildrenListPage } from '../parent/MobileChildrenListPage';
import { MobileChildDetailPage } from '../parent/MobileChildDetailPage';
import { MobileChildEditPage } from '../parent/MobileChildEditPage';

// Desktop pages used as fallbacks for pages without mobile-specific versions
const HomePage = lazy(() => import('@/app/pages/HomePage').then(m => ({ default: m.HomePage })));
const ProfilePage = lazy(() => import('@/features/profile/pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const ActivityDetailPage = lazy(() => import('@/features/parent/pages/ActivityDetailPage'));
const SessionDetailPage = lazy(() => import('@/features/parent/pages/SessionDetailPage'));
const AssignmentDetailPage = lazy(() => import('@/features/parent/pages/AssignmentDetailPage'));
const ParentAssignmentsPage = lazy(() => import('@/features/parent/pages/ParentAssignmentsPage'));

// Fluency pages — full-screen, no mobile nav bar
const FluencyEntryPage = lazy(() => import('@/features/fluency/pages/FluencyEntryPage'));
const FluencySessionPage = lazy(() => import('@/features/fluency/pages/FluencySessionPage'));
const FluencyKidViewPage = lazy(() => import('@/features/fluency/pages/FluencyKidViewPage'));

// Kid device sign-in — full-screen, unauthenticated
const JoinPage = lazy(() => import('@/features/student-auth/pages/JoinPage'));
const ChooserPage = lazy(() => import('@/features/student-auth/pages/ChooserPage'));

export function MobileApp() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route
        path="/fluency/kid/:sessionId"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <FluencyKidViewPage />
          </Suspense>
        }
      />

      {/* Kid device sign-in — unauthenticated, no mobile shell */}
      <Route
        path="/join"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <JoinPage />
          </Suspense>
        }
      />
      <Route
        path="/kid"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <ChooserPage />
          </Suspense>
        }
      />

      <Route element={<ProtectedRoute />}>
        {/* Full-screen routes — no mobile layout shell */}
        <Route path="/story/:id" element={<MobileStoryReader />} />
        <Route path="/quiz/:id" element={<MobileQuizPage />} />
        <Route path="/results/:id" element={<MobileResultsPage />} />
        <Route path="/flashcards/review/:deckId" element={<MobileStudyPage />} />

        {/* Fluency is full-screen — sticky bottom bar handles controls */}
        <Route path="/parent/fluency/start" element={
          <Suspense fallback={<LoadingFallback />}><FluencyEntryPage /></Suspense>
        } />
        <Route path="/parent/fluency/session/:sessionId" element={
          <Suspense fallback={<LoadingFallback />}><FluencySessionPage /></Suspense>
        } />

        {/* Routes with mobile layout shell (bottom nav) */}
        <Route element={<MobileLayout />}>
          <Route path="/" element={
            <Suspense fallback={<LoadingFallback />}><HomePage /></Suspense>
          } />

          {/* Parent routes — canonical paths */}
          <Route path="/parent" element={<MobileParentDashboard />} />
          <Route path="/parent/children" element={<MobileChildrenListPage />} />
          <Route path="/parent/child/:id" element={<MobileChildDetailPage />} />
          <Route path="/parent/child/:childId/edit" element={<MobileChildEditPage />} />
          <Route path="/parent/assignments" element={
            <Suspense fallback={<LoadingFallback />}><ParentAssignmentsPage /></Suspense>
          } />
          <Route path="/parent/child/:id/activity/:itemType/:itemId" element={
            <Suspense fallback={<LoadingFallback />}><ActivityDetailPage /></Suspense>
          } />
          <Route path="/parent/child/:id/session/:sessionId" element={
            <Suspense fallback={<LoadingFallback />}><SessionDetailPage /></Suspense>
          } />
          <Route path="/parent/child/:id/assignment/:assignmentId" element={
            <Suspense fallback={<LoadingFallback />}><AssignmentDetailPage /></Suspense>
          } />

          {/* Legacy /mobile/* paths — redirect to canonical */}
          <Route path="/mobile/parent" element={<MobileParentDashboard />} />
          <Route path="/mobile/parent/dashboard" element={<MobileParentDashboard />} />
          <Route path="/mobile/parent/children" element={<MobileChildrenListPage />} />
          <Route path="/mobile/parent/create-assignment" element={<MobileAssignmentCreator />} />
          <Route path="/mobile/parent/assign/story" element={<MobileAssignmentCreator />} />
          <Route path="/mobile/parent/assign/flashcards" element={<MobileAssignmentCreator />} />
          <Route path="/mobile/parent/child/:childId" element={<MobileChildDetailPage />} />
          <Route path="/mobile/parent/child/:childId/edit" element={<MobileChildEditPage />} />
          <Route path="/mobile/parent/assignments" element={<MobileAssignmentsPage />} />

          {/* Profile */}
          <Route path="/profile" element={
            <Suspense fallback={<LoadingFallback />}><ProfilePage /></Suspense>
          } />

          {/* Student pages */}
          <Route path="/library" element={<MobileLibraryPage />} />
          <Route path="/library/story/create" element={<MobileGenerateStoryPage />} />
          <Route path="/assignments" element={<MobileAssignmentsPage />} />
          <Route path="/flashcards" element={<MobileFlashcardsPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
